import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Shield, 
  Zap, 
  Brain, 
  FileText, 
  MessageSquare, 
  BookOpen,
  Users,
  Target,
  Check
} from 'lucide-react';

const HomePage: React.FC = () => {
  return (
    <div className="bg-white">
      {/* Hero Section with Background Image */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80" 
            alt="AI workflow diagram" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-900 to-blue-900 mix-blend-multiply opacity-90" />
        </div>
        <div className="relative max-w-7xl mx-auto py-32 px-4 sm:py-40 sm:px-6 lg:px-8">
          <div className="md:w-2/3">
            <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Transform Your Financial Workflows
            </h1>
            <p className="mt-6 max-w-3xl text-xl text-cyan-100">
              At Prestige AI, we are revolutionizing the way auditors and finance professionals work by harnessing the power of artificial intelligence. Designed by auditors for auditors, our AI-driven solutions enhance efficiency, accuracy, and compliance in financial reporting, audit procedures, and technical accounting queries.
            </p>
            <p className="mt-4 max-w-3xl text-lg text-cyan-100">
              Our mission is simple: to transform the profession by reducing manual workloads, mitigating human error, restoring work-life balance, and enabling professionals to focus on high-value tasks.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/about"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-cyan-700 bg-white hover:bg-cyan-50 shadow-lg"
              >
                Learn More About Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center px-5 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-cyan-800 shadow-lg"
              >
                Explore Solutions
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Solutions Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base font-semibold text-cyan-600 tracking-wide uppercase">Our Solutions</h2>
            <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Comprehensive AI Solutions for Financial Professionals
            </p>
          </div>

          <div className="mt-16">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {/* Solution 1 */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg text-white">
                      <Brain className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Pay-Per-Use AI Bots</h3>
                  </div>
                  <p className="mt-4 text-gray-600">
                    A comprehensive suite of AI-driven tools designed to enhance financial statement reviews, audit procedures, technical accounting analysis, and compliance checks.
                  </p>
                  <Link
                    to="/solutions"
                    className="mt-4 inline-flex items-center text-cyan-600 hover:text-cyan-700"
                  >
                    Learn more about our AI Bots
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>

              {/* Solution 2 */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg text-white">
                      <FileText className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Customized AI & Automation</h3>
                  </div>
                  <p className="mt-4 text-gray-600">
                    Tailor-made AI-driven automation for firms seeking to optimize workflows, reduce manual effort, and improve decision-making.
                  </p>
                  <a
                    href="mailto:sales@prestigeai.tech"
                    className="mt-4 inline-flex items-center text-cyan-600 hover:text-cyan-700"
                  >
                    Contact our sales team
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </div>

              {/* Solution 3 */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg text-white">
                      <BookOpen className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Technical Opinions</h3>
                  </div>
                  <p className="mt-4 text-gray-600">
                    Providing expert guidance and detailed position papers on complex IFRS and International Auditing Standards queries.
                  </p>
                  <a
                    href="mailto:sales@prestigeai.tech"
                    className="mt-4 inline-flex items-center text-cyan-600 hover:text-cyan-700"
                  >
                    Request technical support
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base font-semibold text-cyan-600 tracking-wide uppercase">Why Choose Prestige AI</h2>
            <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Not Just Another Automation Provider
            </p>
            <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
              We are redefining how audits and financial reviews are performed.
            </p>
          </div>

          <div className="mt-16">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {/* Reason 1 */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg text-white">
                    <Users className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Designed by Experts & Refined by Industry</h3>
                </div>
                <p className="mt-4 text-gray-600">
                  Led by a Chartered Accountant and former audit partner, with extensive input from experienced professionals and firms.
                </p>
              </div>

              {/* Reason 2 */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg text-white">
                    <Target className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Focused on Practical Implementation</h3>
                </div>
                <p className="mt-4 text-gray-600">
                  Addressing real-world challenges faced by auditors and finance professionals.
                </p>
              </div>

              {/* Reason 3 */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg text-white">
                    <Zap className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Driven by Innovation & Affordability</h3>
                </div>
                <p className="mt-4 text-gray-600">
                  We integrate the latest cutting-edge AI models from multiple leading LLM providers, ensuring access to the best available technology without the need for costly infrastructure. Our pricing model is pay-per-use—no sign-up costs, no monthly subscriptions.
                </p>
              </div>

              {/* Reason 4 */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg text-white">
                    <MessageSquare className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Dedicated Support</h3>
                </div>
                <p className="mt-4 text-gray-600">
                  We understand the critical nature of financial and audit work. That's why we offer dedicated support to help you maximize the value of our AI-driven solutions.
                </p>
              </div>

              {/* Reason 5 */}
              <div className="bg-white rounded-lg shadow-lg p-6 md:col-span-2">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg text-white">
                    <Shield className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Confidential and Secure</h3>
                </div>
                <p className="mt-4 text-gray-600">
                  Your data security and privacy are our top priorities. We employ enterprise-grade security measures and strict data handling protocols.
                </p>
                <Link
                  to="/confidentiality"
                  className="mt-4 inline-flex items-center text-cyan-600 hover:text-cyan-700"
                >
                  Learn more about our security measures
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-700">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">Ready to transform your workflow?</span>
            <span className="block text-cyan-200">Start your journey with Prestige AI today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-cyan-600 bg-white hover:bg-cyan-50"
              >
                Get started
              </Link>
            </div>
            <div className="ml-3 inline-flex rounded-md shadow">
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
              >
                Learn more
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;